import { Box, Grid, Table, TableHead, TableBody, TableRow, TableCell, Typography, makeStyles } from '@material-ui/core';
import SlideFrame, { ContestantHeading } from './SlideFrame';
import useTableRules from '../UI/useTableRules';
import { CONTESTANT_COLORS, CONTESTANT_ROW_COLORS } from './streamPalette';
import { ACCEPTED_COLOR, REJECTED_COLOR } from '../tournament/participant/palette';
import durationToString from '../../utils/durationToString';

const useStyles = makeStyles({
    // a table is only as wide as what is in it, and sits in the middle of its
    // half of the slide rather than stretched across it
    table: {
        width: 'auto'
    },
    // The rows are set tight for their size: a contestant coming through the
    // loser bracket of a 16 player tournament arrives at the final with seven
    // matches behind them, and all of them have to be on the slide at once.
    cell: {
        fontSize: '1.75rem',
        whiteSpace: 'nowrap',
        padding: '8px 14px'
    },
    head: {
        fontSize: '1.6rem',
        whiteSpace: 'nowrap',
        padding: '8px 14px'
    },
    // a long handle is cut rather than allowed to push the table over the width
    // of its half of the slide
    opponent: {
        display: 'block',
        maxWidth: 190,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }
});

// How long a match ran. One still being played has no length recorded yet, so
// what it has run for so far is counted off its start.
function elapsed(row) {
    if (row.length != null) return row.length;
    if (row.startTime == null) return null;
    return Math.max(0, Date.now() - row.startTime);
}

// The two contestants' runs through the tournament, a table to each of them in
// their own colour. Their names are picked out wherever one stands as the other's
// opponent, which is how the game the two have already played reads off the pair.
function MatchHistorySlide(props) {
    const classes = useStyles();
    const rules = useTableRules();
    const sides = [
        { name: props.contestant1, statistics: props.statistics1 },
        { name: props.contestant2, statistics: props.statistics2 }
    ];

    const opponentColor = opponent => {
        if (opponent === props.contestant1) return CONTESTANT_COLORS[0];
        if (opponent === props.contestant2) return CONTESTANT_COLORS[1];
        return 'inherit';
    };

    return (
        <SlideFrame eyebrow={props.tournamentName} matchLabel={props.matchLabel} title='Match History'>
            <Grid container spacing={4}>
                {sides.map((side, index) => {
                    // in the order they were played, which is not the order of the
                    // bracket: a loser bracket match can be played before the
                    // winner bracket one it follows in the numbering
                    const rows = (side.statistics?.matches ?? [])
                        .filter(match => match.started)
                        .sort((lhs, rhs) => (lhs.startTime ?? 0) - (rhs.startTime ?? 0));
                    return (
                        <Grid item xs={6} key={index}>
                            <ContestantHeading contestant={index} name={side.name} variant='h4' />
                            {rows.length === 0 ?
                                <Typography color='textSecondary' align='center'>
                                    No match played yet
                                </Typography> :
                                <Box display='flex' justifyContent='center'>
                                    <Table className={`${classes.table} ${rules.ruled}`}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.head}> Match </TableCell>
                                                <TableCell className={classes.head}> Opponent </TableCell>
                                                <TableCell className={classes.head} align='right'> Time </TableCell>
                                                <TableCell className={classes.head} align='right'> Result </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map(row => (
                                                <TableRow
                                                    key={row.matchId}
                                                    style={{ backgroundColor: CONTESTANT_ROW_COLORS[index] }}
                                                >
                                                    <TableCell className={classes.cell}> {row.label} </TableCell>
                                                    <TableCell
                                                        className={classes.cell}
                                                        style={{ color: opponentColor(row.opponent) }}
                                                        title={row.opponent ?? undefined}
                                                    >
                                                        <span className={classes.opponent}>
                                                            {row.opponent ?? 'TBD'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className={classes.cell} align='right'>
                                                        {durationToString(elapsed(row))}
                                                    </TableCell>
                                                    <TableCell
                                                        className={classes.cell}
                                                        align='right'
                                                        style={{ color: row.result === 'W' ? ACCEPTED_COLOR : (row.result === 'L' ? REJECTED_COLOR : 'inherit') }}
                                                    >
                                                        {row.result == null ? '—' : `(${row.result}) ${row.solved}:${row.opponentSolved}`}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            }
                        </Grid>
                    );
                })}
            </Grid>
        </SlideFrame>
    );
}

export default MatchHistorySlide;
